import type { JSX } from "preact/jsx-runtime";
import type { TargetedEvent } from "preact/compat";
import { useState } from "preact/hooks";
import { clsx } from "clsx";
import { useViewer } from "~/viewer/context.tsx";
import UploadImageIcon from "~/components/icons/upload-image.tsx";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import classes from "./upload-image-field.module.css";

type UploadImageFieldProps = {
	readonly name: string;
	readonly label: string;
	readonly onDropFile: (file: File | undefined) => void;
	readonly onInputChange?: () => void;
	readonly required?: boolean;
	readonly disabled?: boolean;
};

function UploadImageField({
	name,
	label,
	onDropFile,
	onInputChange: externalOnInputChange,
	required,
	disabled,
}: UploadImageFieldProps) {
	const t = useTranslator();
	const { isUsingFacebookBrowser } = useViewer();

	// Local representation of file
	const [file, setFile] = useState<File>();

	// Drag and drop files
	const [isHighlightedForDrop, setHighlightedForDrop] = useState(false);
	const onDragEnterOrOver = () => {
		setHighlightedForDrop(true);
	};
	const onDragLeave = () => {
		setHighlightedForDrop(false);
	};
	const onDrop = (e: JSX.TargetedDragEvent<HTMLLabelElement>) => {
		e.preventDefault();
		setHighlightedForDrop(false);
		if (!e.dataTransfer) {
			return;
		}

		const { files } = e.dataTransfer;
		if (files.length > 0) {
			const file = files[0];
			setFile(file);
			onDropFile(file);
		} else {
			onDropFile(undefined);
			setFile(undefined);
		}
	};

	// File input change
	const onChange = (e: TargetedEvent<HTMLInputElement>) => {
		const {
			currentTarget: { files },
		} = e;
		const file = files ? files[0] : undefined;
		setFile(file);
		externalOnInputChange?.();
	};

	const fieldId = `${name}-field`;

	return (
		<label
			htmlFor={fieldId}
			onDragEnter={onDragEnterOrOver}
			onDragOver={onDragEnterOrOver}
			onDragLeave={onDragLeave}
			onDrop={onDrop}
			className={clsx(
				classes["upload-image-label"],
				isHighlightedForDrop && classes["hover-active"],
			)}
		>
			{label}
			{file && (
				<div className={classes["current-file"]}>
					{t("Current: {name}", { name: file.name })}
				</div>
			)}
			<UploadImageIcon />
			<input
				id={fieldId}
				type="file"
				name={name}
				// Issue with accept attr for in-app fb browser
				// https://stackoverflow.com/questions/27000708/
				// file-upload-control-not-working-in-facebook-in-app-browser
				accept={isUsingFacebookBrowser ? undefined : "image/*"}
				required={required}
				onChange={onChange}
				disabled={disabled}
			/>
		</label>
	);
}

export default UploadImageField;
